/*
 * Typed history for replay: the value's own type survives, and many symbols
 * interleave by time.
 *
 * WHY A SECOND HISTORY CLIENT. `queryRange` in connect-client.ts is a CHART
 * client: it projects every sample to a number and DROPS whatever is not one —
 * a bool becomes 0/1, a string vanishes, quality is discarded. A statechart
 * replayed against history needs the value the plant actually published (a
 * string mode, a bool interlock) or its guards evaluate against nothing. The
 * wire already carries the type; this is the client that keeps it.
 *
 * WHY A MERGE. The historian answers one query at a time, ordered (symbol, ts),
 * and its cursor is only sound for a SINGLE symbol per request — a truncated
 * multi-symbol reply cuts mid-symbol and later symbols have not emitted at all.
 * So the reader pages one symbol at a time, and the chronological interleaving
 * that a replay fundamentally needs happens here, not on the server.
 *
 * This module is pure so it can be tested without a transport; the RPC-bound
 * reader (`queryRangeTyped`) lives in connect-client.ts and calls into it.
 */

export interface TypedSample {
  /** Display path (controller scope, decoded) or the verbatim variable id
   *  (model scope) — the same key the caller asked with. */
  symbol: string;
  /** The producer's own timestamp, ns since epoch, at FULL precision. Two
   *  samples a few microseconds apart must replay in the order they happened;
   *  the chart client's ms truncation would make them a tie. */
  tsNs: bigint;
  /** The value as published: boolean | number | string, or null for raw/unknown. */
  value: boolean | number | string | null;
  /** SymbolValue.quality — 0 good, 1 uncertain, 2 bad — surfaced, not discarded. */
  quality: number;
}

export interface TypedHistory {
  /** Every sample of every requested symbol, ascending by tsNs. */
  samples: TypedSample[];
  /** What the backend said that was NOT the routine row count. Today that is
   *  the api-gateway's retention narrowing ("window narrowed to the N day(s)
   *  your plan retains …"). A replay that starts at a different instant than
   *  asked must say so, and this is where it learns that. Distinct, in
   *  first-seen order. */
  notes: string[];
  /** Symbols whose cursor walk hit the page cap before the window was
   *  exhausted. Their tail is missing, so the caller must not present the
   *  run as complete. */
  truncated: string[];
}

/**
 * The historian's per-page success line — "Returned 1284 sample row(s) for the
 * requested range." — is routine and is not a note. Result.summary is prose by
 * contract, so this pins the template rather than a code; if the wording moves,
 * test/history-typed.test.ts moves with it, loudly.
 */
export function isRoutineHistorianSummary(summary: string): boolean {
  return /^Returned \d+ sample row\(s\)/.test(summary);
}

/**
 * Interleave per-symbol streams into one chronological stream.
 *
 * Each input stream is one symbol's samples as the historian returned them
 * (ascending ts). They are sorted here anyway — stably — so an unsorted caller
 * still gets a correct answer rather than a subtly wrong one. Ties on tsNs are
 * broken by stream order, then by position within the stream: deterministic,
 * which the replay's "run it twice, get the same trace" promise depends on.
 *
 * Exact duplicates — same symbol, same tsNs, same value, same quality — are
 * dropped. Ingest is at-least-once, so a redelivered batch can land twice, and
 * a replay that saw the row twice would fire an event twice for one change. A
 * genuine re-publish of an identical value at the identical nanosecond is
 * indistinguishable from that and is dropped too; nothing downstream can tell
 * them apart either.
 */
export function mergeByTime(streams: TypedSample[][]): TypedSample[] {
  const sorted = streams.map((s) => s.slice().sort(byTs));
  const heap = new MinHeap();
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].length > 0) heap.push({ ts: sorted[i][0].tsNs, stream: i, idx: 0 });
  }
  const out: TypedSample[] = [];
  const last = new Map<string, TypedSample>();
  while (heap.size > 0) {
    const head = heap.pop();
    const stream = sorted[head.stream];
    const s = stream[head.idx];
    const prev = last.get(s.symbol);
    const dup = prev !== undefined && prev.tsNs === s.tsNs && prev.value === s.value && prev.quality === s.quality;
    if (!dup) {
      out.push(s);
      last.set(s.symbol, s);
    }
    const next = head.idx + 1;
    if (next < stream.length) heap.push({ ts: stream[next].tsNs, stream: head.stream, idx: next });
  }
  return out;
}

/**
 * Run `fn` over every item with at most `limit` in flight, results in item
 * order. The reader walks each symbol's pages as its own cursor; this lets a
 * few walks proceed at once without every symbol of a wide model hitting the
 * historian in one burst.
 *
 * THE WALKS SHARE ONE ABORT. Each `fn` is handed a signal that fires when the
 * caller's `signal` does, or when any walk fails — so a failure takes the
 * others down with it instead of leaving them to finish page by page for a
 * result nobody will read. The first failure rejects the call and no further
 * walk is started.
 */
export async function inFlightLimit<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number, signal: AbortSignal) => Promise<R>,
  signal?: AbortSignal,
): Promise<R[]> {
  const ac = new AbortController();
  const onAbort = () => ac.abort(signal?.reason);
  if (signal?.aborted) onAbort();
  else signal?.addEventListener('abort', onAbort, { once: true });
  const out: R[] = new Array(items.length);
  let next = 0;
  try {
    const lanes = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
      while (!ac.signal.aborted && next < items.length) {
        const i = next++;
        try {
          out[i] = await fn(items[i], i, ac.signal);
        } catch (e) {
          ac.abort(e);
          throw e;
        }
      }
    });
    await Promise.all(lanes);
    // Aborted from outside between items: nothing failed, but the list is not whole.
    if (ac.signal.aborted) throw ac.signal.reason ?? new Error('aborted');
    return out;
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }
}

function byTs(a: TypedSample, b: TypedSample): number {
  return a.tsNs < b.tsNs ? -1 : a.tsNs > b.tsNs ? 1 : 0;
}

/** One cursor into one stream, keyed for the heap. */
interface Head {
  ts: bigint;
  stream: number;
  idx: number;
}

/** Ordered (ts, stream, idx) — the tie-break IS the determinism guarantee. */
function before(a: Head, b: Head): boolean {
  if (a.ts !== b.ts) return a.ts < b.ts;
  if (a.stream !== b.stream) return a.stream < b.stream;
  return a.idx < b.idx;
}

/** A binary min-heap of stream heads. k streams → O(N log k) for the merge;
 *  a replay over a few dozen symbols never notices, a few thousand does. */
class MinHeap {
  private readonly a: Head[] = [];

  get size(): number {
    return this.a.length;
  }

  push(h: Head): void {
    const a = this.a;
    a.push(h);
    let i = a.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (!before(a[i], a[p])) break;
      [a[i], a[p]] = [a[p], a[i]];
      i = p;
    }
  }

  pop(): Head {
    const a = this.a;
    const top = a[0];
    const tail = a.pop() as Head;
    if (a.length > 0) {
      a[0] = tail;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < a.length && before(a[l], a[m])) m = l;
        if (r < a.length && before(a[r], a[m])) m = r;
        if (m === i) break;
        [a[i], a[m]] = [a[m], a[i]];
        i = m;
      }
    }
    return top;
  }
}
